/**
 * Short-lived Ops AI chat draft (sessionStorage).
 *
 * Lets the user leave Create with AI for BOQ & drawings and come back to the
 * same proposal instead of restarting the conversation.
 */

const STORAGE_KEY = 'housingram.opsAiChatDraft.v1';
const TTL_MS = 60 * 60 * 1000;
const MAX_MESSAGES = 30;

function readRaw() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function sameScope(draft, { surfaceId, projectId }) {
  if (!draft) return false;
  if (draft.surfaceId !== surfaceId) return false;
  const a = draft.projectId == null ? null : Number(draft.projectId);
  const b = projectId == null || projectId === '' ? null : Number(projectId);
  return a === b;
}

export function saveOpsAiDraft(draft) {
  if (!draft?.surfaceId) return;
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        surfaceId: draft.surfaceId,
        projectId: draft.projectId ?? null,
        savedAt: Date.now(),
        messages: Array.isArray(draft.messages) ? draft.messages.slice(-MAX_MESSAGES) : [],
        applyPayload: draft.applyPayload ?? null,
        questions: Array.isArray(draft.questions) ? draft.questions : [],
        answeredIds: Array.isArray(draft.answeredIds) ? draft.answeredIds : [],
        questionsDismissed: Boolean(draft.questionsDismissed),
        // Doc text is already embedded in RAG — keep names only to stay under quota.
        docNames: Array.isArray(draft.docNames) ? draft.docNames : []
      })
    );
  } catch {
    // Quota or private-mode failures must never break the chat.
  }
}

export function loadOpsAiDraft({ surfaceId, projectId }) {
  const draft = readRaw();
  if (!sameScope(draft, { surfaceId, projectId })) return null;
  if (!draft.savedAt || Date.now() - draft.savedAt > TTL_MS) {
    clearOpsAiDraft();
    return null;
  }
  const hasContent =
    (Array.isArray(draft.messages) && draft.messages.some((m) => !m.localOnly)) ||
    Boolean(draft.applyPayload?.templates?.length);
  return hasContent ? draft : null;
}

export function clearOpsAiDraft() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
